/* eslint-disable @typescript-eslint/no-explicit-any */

import { JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { updateAttachmentsValidation } from '../../store/attachments';
import {
  clearAttachmentError,
  clearFinancialAttachmentError,
  setAttachmentError,
  setfinancialAttachmentCardError
} from '../../store/fieldError';
import AttachmentList from '../AttachmentList';
import { ErrorIcon, ErrorValidation } from '../ErrorValidation';

interface DragDropBoxProps {
  disabled?: boolean;
  handleUpload: () => void;
  t: (key: string) => string;
}

const DragDropBox = ({ disabled, handleUpload, t }: DragDropBoxProps): JSX.Element => (
  <Box
    className="sprout-fileupload drag-and-drop"
    sx={{
      mb: '16px',
      width: '100%',
      display: 'flex',
      flexDirection: {
        xs: 'row',
        sm: 'column'
      },
      justifyContent: 'center',
      alignItems: 'center',
      gap: { xs: '8px', sm: '16px' },
      padding: { xs: '15px 0', sm: '31px 0' },
      borderRadius:
        'var(--corner-radius-base, 8px)',
      border:
        '1px dashed var(--Border-border-primary, #DBDDDC)',
      background:
        'var(--Background-bg-surface, #FFF)',
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled
        ? 'none'
        : 'auto',
      filter: disabled
        ? 'grayscale(0.5)'
        : 'none'
    }}
    aria-disabled={disabled}
  >
    <CloudUploadIcon
      sx={{ width: 36, height: 36, color: '#707773' }}
    />

    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Typography
        variant="body1"
        align="center"
        sx={{ color: 'var(--neutral-500)' }}
      >
        <Link
          component="button"
          type="button"
          onClick={disabled ? undefined : handleUpload}
          underline="always"
          disabled={disabled}
        >
          {t('buttons.uploadLink')}
        </Link>
      </Typography>

      <Typography
        variant="body2"
        align="center"
        sx={{ color: 'var(--neutral-500)' }}
      >
        {t('buttons.uploadSupportingText')}
      </Typography>
    </Box>
  </Box>
);


const uploadButtonSx = {
  display: 'flex',
  padding:
    'var(--padding-padding-base, 8px) var(--padding-padding-lg, 16px)',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 'var(--spacing-spacing-base, 8px)',
  borderRadius: 'var(--corner-radius-base, 8px)',
  background: 'var(--sprout-bg-interactive, #00843D)',
  color: 'var(--sprout-text-on-primary, #fff)',
  textTransform: 'none',
  font: 'var(--sprout-font-body1-bold)',
  maxWidth: '100%',

  '&:hover': {
    background: 'var(--sprout-bg-interactive, #00843D)',
    opacity: 0.92
  },
  '&.Mui-disabled': {
    background: 'var(--neutral-100) !important',
    color: 'var(--neutral-400) !important'
  }
};

interface UploadButtonProps {
  shouldShow?: boolean;
  showUploadButton?: boolean;
  handleUpload: () => void;
  disableUploadButton?: boolean;
  t: (key: string) => string;
}

const UploadButton = ({ 
  shouldShow, 
  showUploadButton, 
  handleUpload, 
  disableUploadButton, 
  t 
}: UploadButtonProps): JSX.Element => {
  const isUploadDocument = shouldShow && showUploadButton;
  const buttonLabel = isUploadDocument
    ? t('buttons.uploadDocument') 
    : t('buttons.uploadNewDocument');

  return (
    <Button
      type="button"
      onClick={handleUpload}
      variant="contained"
      disabled={isUploadDocument ? !!disableUploadButton : undefined}
      sx={{
        ...uploadButtonSx,
        width: { xs: '100%', sm: 'auto' }
      }}
    >
      {buttonLabel}
    </Button>
  );
};

export const checkAttachmentError = (
  props: any,
  attachmentCard: Record<string, any>,
  t: (key: string) => string,
  customerOptoutAttachments: boolean
): void => {
  const { required, attachments, field, dispatch } =
    props;

  if (field === 'financialCardAttachment') {
    if (
      attachments.length === 0 &&
      !customerOptoutAttachments
    ) {
      dispatch(
        setfinancialAttachmentCardError({
          [field]: t('errors.required')
        })
      );
    } else if (
      (attachments.length === 0 &&
        customerOptoutAttachments) ||
      (attachments.length !== 0 &&
        !customerOptoutAttachments)
    ) {
      dispatch(clearFinancialAttachmentError(field));
    }
  }

  if (required) {
    if (attachments.length === 0) {
      dispatch(
        setAttachmentError({
          [field]: t('errors.required')
        })
      );
    } else {
      dispatch(clearAttachmentError(field));
    }
  } else if (attachmentCard[field]) {
    dispatch(clearAttachmentError(field));
  }
};

interface AttachmentUploadProps {
  handleUpload: () => void;
 handleRemove: (fileName: string) => void;
  handleDownload: (
    hcId: string | number | undefined,
    fileName: string,
    dispatch: unknown
  ) => void;

  isAdditionalAttachment?: boolean;
  shouldShow?: boolean;
  uploadButtonLabel?: string;
  required?: boolean;
  dispatch: (action: any) => void;
  field: string;
  attachments: any[];
  store?: any;
  disableUploadButton?: boolean;
  showUploadButton?: boolean;
  disabled?: boolean;
  isReview?: boolean;
  fontSize?: string;
  labelMargin?: string;
  listRight?: boolean;
  [key: string]: any;
}

function AttachmentUpload(
  props: AttachmentUploadProps
): JSX.Element {
  const {
    handleUpload,
    isAdditionalAttachment,
    shouldShow,
    uploadButtonLabel,
    required,
    dispatch,
    field,
    attachments,
    store = {},
    disableUploadButton,
    showUploadButton
  } = props;

  const { t } = useTranslation();

  const {
    customer: {
      id: hcId = '',
      customerOptoutAttachments = false
    } = {},
    fieldError: { attachmentCard = {} } = {}
  } = store;

  useEffect(() => {
    dispatch(
      updateAttachmentsValidation({
        key: field,
        required
      })
    );

    checkAttachmentError(
      props,
      attachmentCard,
      t,
      customerOptoutAttachments
    );
  }, [
    attachments?.length,
    required,
    disableUploadButton,
    showUploadButton,
    customerOptoutAttachments,
    field
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        margin: 0,
        marginTop: props.isReview ? '16px' : 0,
        alignSelf: 'stretch',
        width: '100%'
      }}
    >
      {uploadButtonLabel && (
        <FormLabel
          required={required}
          sx={{
            display: 'block',
            fontSize: props.fontSize ?? '14px',
            fontWeight: 700,
            color:
              'var(--sprout-text-primary, #101C16)',
            margin:
              props.labelMargin ?? '0 0 16px',
            '& .MuiFormLabel-asterisk': {
              color:
                'var(--sprout-text-danger, #CD0C14)'
            }
          }}
        >
          {uploadButtonLabel}
        </FormLabel>
      )}

      {!shouldShow &&
        attachmentCard &&
        attachmentCard[field] && (
          <ErrorValidation>
            <ErrorIcon />
            <p>{t('errors.required')}</p>
          </ErrorValidation>
        )}

      {isAdditionalAttachment ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch'
          }}
        >
          <DragDropBox 
            disabled={props.disabled} 
            handleUpload={handleUpload} 
            t={t} 
          />

          <Box
            sx={{
              flex: '1 1 0',
              marginBottom: '16px',
              minWidth: { xs: '100%', sm: 0 }
            }}
          >
          <AttachmentList
            hcId={hcId}
            {...props}
            data-testid="attachment-list"
          />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            mt: uploadButtonLabel ? 0 : '16px',
            display: 'flex',
            width: '100%',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'flex-start',
            flexDirection: {
              xs: 'column',
              sm: props.listRight
                ? 'row'
                : 'column'
            }
          }}
        >
          <UploadButton 
            shouldShow={shouldShow}
            showUploadButton={showUploadButton}
            handleUpload={handleUpload}
            disableUploadButton={disableUploadButton}
            t={t}
          />

           <Box
            sx={{
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              marginBottom: '16px',
              // overflowX: 'auto'
            }}
          >
          <AttachmentList
            hcId={hcId}
            {...props}
            data-testid="attachment-list"
          />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default AttachmentUpload;
